import type {
    ClassicalAPIResponse,
    ClassicalAPIResponseHeader,
    ClassicalFooterMeta,
    ClassicalListItem,
    ClassicalListViewPage,
    ClassicalPaginatedResponse,
    ClassicalShelfItem,
} from '@jet-app/music-classical/api/types';
import type { LocalizationKey } from '@jet-app/music-classical/dependencies/localization';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies/object-graph-type';
import {
    inject,
    type ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import type { Opt } from '@jet/environment/types/optional';
import { deriveDuration } from '../seo/duration';

export function getLibraryPlaylistActionPayload(
    apiResponse: ClassicalAPIResponse,
): { [key: string]: any } {
    const responseHeader = apiResponse?.body
        ?.header as unknown as ClassicalAPIResponseHeader;
    const responseBody = apiResponse?.body as unknown as ClassicalListViewPage;
    const paginatedResponse =
        responseBody?.firstPage as ClassicalPaginatedResponse;
    const trackItems =
        paginatedResponse?.items as unknown as ClassicalListItem[];

    return {
        title: responseHeader?.title,
        description: responseHeader?.description?.standard,
        librarySongs: trackItems
            .filter((item) => item.type === 'track')
            .map((item) => item.payload),
        offset: paginatedResponse?.offset ?? -1,
    };
}

export function localizedTrackCount(
    trackCount: number,
    objectGraph: ObjectGraph,
): string | null {
    const localization = inject(ObjectGraphType.localization, objectGraph);

    if (trackCount === 1) {
        return localization.string(
            'ASE.Web.MusicClassicalClientJS.Tracks.one' as LocalizationKey,
        );
    }

    return localization.string(
        'ASE.Web.MusicClassicalClientJS.Tracks.many' as LocalizationKey,
        { count: `${trackCount}` },
    );
}

export function tracklistDescription(
    count: number,
    duration: number,
    objectGraph: ObjectGraph,
): string | null {
    const localizedNumberOfItems: Opt<string> = localizedTrackCount(
        count,
        objectGraph,
    );
    const localizedDuration = deriveDuration(duration, objectGraph);
    const localization = inject(ObjectGraphType.localization, objectGraph);

    return localizedNumberOfItems && localizedDuration
        ? localization.string(
              'ASE.Web.MusicClassical.ContentA.Comma.ContentB' as LocalizationKey,
              {
                  contentA: localizedNumberOfItems,
                  contentB: localizedDuration,
              },
          )
        : null;
}

export function getFooterTrackMetadata(
    items: ClassicalShelfItem[],
): ClassicalFooterMeta {
    return items.reduce(
        (acc, curr) => {
            if (curr.type === 'track') {
                acc.count++;
                acc.duration += curr.durationMs;
            }

            return acc;
        },
        { count: 0, duration: 0 },
    );
}
